import { useCallback, useEffect, useRef, useState, useTransition } from 'react'
import dayjs, { DayjsInput } from 'utils/dayjs'
import { EventAPIResponse, UseCalendarOptions } from '../types'
import { MonthProps } from '~types'
import { EventService } from '../services/eventServices'
import { generateCalendarStartAndEndDate } from '../utils'
import { useSearchParams } from 'next/navigation'
import { DATE_FORMAT } from '~constants'

export const useCalendar = (options: UseCalendarOptions) => {
  // State variables for selected date, filters, artist IDs, and events
  const searchParams = useSearchParams()
  const { initialDate, artistIds, endDate } = options
  const [selectedDate, setSelectedDate] = useState(initialDate || new Date())
  const [selectedMonth, setSelectedMonth] = useState<MonthProps>({
    label: dayjs(initialDate as string).format(DATE_FORMAT.MONTH_YEAR_LABEL),
    value: dayjs(initialDate as string).format(DATE_FORMAT.MONTH_YEAR_VALUE),
    shortLabel: dayjs(initialDate as string).format(DATE_FORMAT.MONTH_YEAR_SHORT)
  })
  const [errors, setErrors] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [filteredItems, setFilteredItems] = useState<EventAPIResponse[]>([])
  const [activeMonthList, setActiveMonthList] = useState<string[]>([])

  const [isPending, startTransition] = useTransition()

  const handleSelect = newValue => {
    startTransition(() => {
      setSelectedDate(newValue)
    })
  }

  // Function to generate calendar start and end dates based on selected date
  const calendarDates = useCallback(
    (selectedDate: DayjsInput) => {
      if (!!endDate) {
        return {
          startDate: dayjs(initialDate as string)?.format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR),
          endDate: dayjs(endDate as string).format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)
        }
      } else {
        return generateCalendarStartAndEndDate(selectedDate)
      }
    },
    [selectedDate]
  )

  // Create Service instance
  const eventService = new EventService()

  const lastFetchParams = useRef<{ startDate: string; endDate: string; artistIds: string } | null>(null)

  // Fecth events based on selected date and filters
  const fetchEvents = async (selectedDate: DayjsInput) => {
    try {
      setLoading(true)
      setErrors(null)
      const { startDate, endDate } = calendarDates(selectedDate)

      const currentParams = {
        startDate,
        endDate,
        artistIds: (artistIds?.split(',') || []).sort().join(',')
      }

      // If same as last fetched, skip API call
      if (
        lastFetchParams.current &&
        lastFetchParams.current.startDate === startDate &&
        lastFetchParams.current.endDate === endDate &&
        lastFetchParams.current.artistIds === currentParams.artistIds
      ) {
        setLoading(false)
        return
      }

      setFilteredItems([])

      // Save current parameters
      lastFetchParams.current = currentParams

      const { formattedEvents, activeMonthList } = await eventService.getEventsFromEventEngine(
        startDate,
        endDate,
        artistIds
      )
      setFilteredItems(formattedEvents)

      if (formattedEvents.length > 0) {
        setLoading(false) // only after we actually have data
      }

      setActiveMonthList(activeMonthList || [])
    } catch (error) {
      setErrors('Failed to fetch events')
    }
  }

  const initializedFromURL = useRef(false)
  // useEffect to fetch events when selectedDate or filters change
  useEffect(() => {
    // Replaces dashes with forward slashes to address Safari parsing bug
    const startDateFromURL = searchParams.get('startdate')?.replace(/-/g, '/')

    if (startDateFromURL && !initializedFromURL.current) {
      const parsedDate = dayjs(startDateFromURL)
      setSelectedMonth({
        label: parsedDate.format(DATE_FORMAT.MONTH_YEAR_LABEL),
        value: parsedDate.format(DATE_FORMAT.MONTH_YEAR_VALUE),
        shortLabel: parsedDate.format(DATE_FORMAT.MONTH_YEAR_SHORT)
      })
      initializedFromURL.current = true
      handleSelect(startDateFromURL)
      fetchEvents(startDateFromURL)
    } else {
      const parsedDate = dayjs(selectedDate as string)
      setSelectedMonth({
        label: parsedDate.format(DATE_FORMAT.MONTH_YEAR_LABEL),
        value: parsedDate.format(DATE_FORMAT.MONTH_YEAR_VALUE),
        shortLabel: parsedDate.format(DATE_FORMAT.MONTH_YEAR_SHORT)
      })
      fetchEvents(selectedDate)
    }
  }, [selectedDate, searchParams?.get('startdate')])

  return {
    selectedDate,
    setSelectedDate: handleSelect,
    artistIds,
    errors,
    loading,
    filteredItems,
    activeMonthList,
    selectedMonth
  }
}
